import React, { useState } from 'react';
import {
 FileText,
 Plus,
 Edit,
 Trash2,
 CheckCircle,
 XCircle,
 Eye,
 Star,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect, DataTable } from '../../components/shared';
import { useAdminBlogPosts } from '../../hooks/useAdminBlogPosts';
import { useToastContext } from '../../contexts/ToastContext';
import CreateBlogPostModal from '../../components/admin/CreateBlogPostModal';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminBlogPosts = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [editingPost, setEditingPost] = useState(null);
 const { success, error: showError } = useToastContext();

 const {
 posts,
 isLoading,
 error,
 filters,
 handleFilterChange,
 statistics,
 createPost,
 isCreating,
 updatePost,
 isUpdating,
 toggleStatus,
 isToggling,
 deletePost,
 isDeleting
 } = useAdminBlogPosts();

 const columns = [
 {
 key: 'title',
 label: 'Title',
 sortable: true,
 width: 'w-64',
 render: (post) => (
 <div>
 <p className="text-sm font-medium text-ink truncate">
 {post.title || 'N/A'}
 </p>
 {post.is_featured && (
 <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
 <Star className="w-3 h-3 fill-current" />
 Featured
 </span>
 )}
 </div>
 )
 },
 {
 key: 'category',
 label: 'Category',
 sortable: true,
 width: 'w-32',
 render: (post) => (
 <span className="text-sm text-ink capitalize">
 {post.category || 'N/A'}
 </span>
 )
 },
 {
 key: 'author_name',
 label: 'Author',
 sortable: true,
 width: 'w-40',
 render: (post) => (
 <span className="text-sm text-ink">
 {post.author_name || 'N/A'}
 </span>
 )
 },
 {
 key: 'views',
 label: 'Views',
 sortable: true,
 width: 'w-24',
 render: (post) => (
 <span className="text-sm text-ink flex items-center gap-1">
 <Eye className="w-4 h-4" />
 {post.views || 0}
 </span>
 )
 },
 {
 key: 'is_published',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (post) => (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
 post.is_published
 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
 : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
 }`}>
 {post.is_published ? 'Published' : 'Draft'}
 </span>
 )
 }
 ];

 const actions = [
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 onClick: (post) => {
 setEditingPost(post);
 setShowCreateModal(true);
 }
 },
 {
 label: post => post.is_published ? 'Unpublish' : 'Publish',
 icon: post => post.is_published ? XCircle : CheckCircle,
 variant: 'default',
 onClick: (post) => {
 toggleStatus(post.id, {
 onSuccess: () => {
 success('Status Updated', `Post ${post.is_published ? 'unpublished' : 'published'} successfully!`);
 },
 onError: (err) => showError('Status Update Failed', err.message || 'Failed to update status.')
 });
 },
 disabled: isToggling
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (post) => {
 if (window.confirm('Are you sure you want to delete this blog post?')) {
 deletePost(post.id, {
 onSuccess: () => success('Post Deleted', 'Blog post deleted successfully!'),
 onError: (err) => showError('Delete Failed', err.message || 'Failed to delete post.')
 });
 }
 },
 disabled: isDeleting
 }
 ];

 const handleSave = (data) => {
 if (editingPost) {
 updatePost(
 { id: editingPost.id, data },
 {
 onSuccess: () => {
 success('Post Updated', 'Blog post updated successfully!');
 setShowCreateModal(false);
 setEditingPost(null);
 },
 onError: (err) => showError('Update Failed', err.message || 'Failed to update post.')
 }
 );
 } else {
 createPost(data, {
 onSuccess: () => {
 success('Post Created', 'Blog post created successfully!');
 setShowCreateModal(false);
 setEditingPost(null);
 },
 onError: (err) => showError('Creation Failed', err.message || 'Failed to create post.')
 });
 }
 };

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Blog Posts"
 subtitle="Manage blog posts and articles"
 />
 <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-surface p-6 text-center shadow-sm">
 <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-xl font-bold text-ink mb-2">Error Loading Blog Posts</h3>
 <p className="text-ink-secondary">
 {error?.response?.data?.message || 'Failed to load blog posts. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Blog Posts"
 subtitle="Manage blog posts and articles"
 actions={
 <Button
 className="rounded-md flex items-center gap-2"
 onClick={() => {
 setEditingPost(null);
 setShowCreateModal(true);
 }}
 >
 <Plus className="w-4 h-4" />
 Create Post
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.blogPosts.title}
 steps={ADMIN_PAGE_GUIDES.blogPosts.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Posts</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{statistics.total_posts || 0}</p>
 </div>
 <FileText className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Published</p>
 <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{statistics.published_posts || 0}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Drafts</p>
 <p className="text-2xl font-bold text-ink-secondary mt-1 tabular-nums">{statistics.draft_posts || 0}</p>
 </div>
 <XCircle className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Featured</p>
 <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">{statistics.featured_posts || 0}</p>
 </div>
 <Star className="w-8 h-8 text-amber-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Views</p>
 <p className="text-2xl font-bold text-ink dark:text-ink-secondary mt-1 tabular-nums">{statistics.total_views || 0}</p>
 </div>
 <Eye className="w-8 h-8 text-ink-secondary" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Status"
 value={filters.is_published}
 onChange={(value) => handleFilterChange('is_published', value)}
 options={[
 { value: '', label: 'All Statuses' },
 { value: 'true', label: 'Published' },
 { value: 'false', label: 'Draft' }
 ]}
 />
 <SearchSelect
 label="Featured"
 value={filters.is_featured}
 onChange={(value) => handleFilterChange('is_featured', value)}
 options={[
 { value: '', label: 'All' },
 { value: 'true', label: 'Featured' },
 { value: 'false', label: 'Not Featured' }
 ]}
 />
 <div className="flex items-end">
 <input
 type="text"
 placeholder="Search posts..."
 value={filters.search || ''}
 onChange={(e) => handleFilterChange('search', e.target.value)}
 className="w-full px-4 py-2 border border-border rounded-xl text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-ink focus:border-ink"
 />
 </div>
 <div className="flex items-end">
 <Button
 onClick={() => {
 handleFilterChange('is_published', '');
 handleFilterChange('is_featured', '');
 handleFilterChange('search', '');
 }}
 variant="outline"
 className="w-full h-[42px] rounded-md"
 >
 Reset Filters
 </Button>
 </div>
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading blog posts…</p>
 </div>
 ) : (
 <DataTable
 data={posts}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search blog posts..."
 onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
 emptyMessage="No blog posts found"
 pageSize={10}
 showPagination={true}
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 {showCreateModal && (
 <CreateBlogPostModal
 isOpen={showCreateModal}
 onClose={() => {
 setShowCreateModal(false);
 setEditingPost(null);
 }}
 onSave={handleSave}
 post={editingPost}
 isEdit={!!editingPost}
 isLoading={isCreating || isUpdating}
 />
 )}
 </div>
 );
};

export default AdminBlogPosts;
